import React, { useEffect, useState } from "react";
import MUITypography from "../../Components/MUITypography";
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import {
  API_DELETE_INVOICE,
  API_GET_INVOICES_LIST,
} from "../../Util/URLConstants";
import StorageFactory from "../../Util/StorageFactory";
import {
  ACCESS_RESTRICTED_MESSAGE,
  SESSION_USER_COUNTRY_DATA_KEY,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
} from "../../Util/VMConstants";
import UpdatePaymentDrawer from "../EnquiryDetails/Invoice/UpdatePaymentDrawer";
import {
  checkIsPermissionExist,
  formatAmount,
  formatDateOnlyFromTDate,
  formatDateYYYYMMDD,
  formatOnlyDate,
  formattedDateTime,
  renderSNo,
} from "../../Util/UtilHelper";
import { canMutateRecord, RECORD_OWNERSHIP_DENIED_MESSAGE } from "../../Util/recordAuthorization";
import VMFilters from "../../Components/VMFilters";
import NoPermission from "../../Components/NoPermission";
import ReusableDataGrid from "../../Components/ReusableDataGrid";
import CreatedByCell from "../../Components/Custom/CreatedByCell";
import { align_center, align_left, vmPageTitleSx } from "../../Styles/VMStyles";

dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

function VmInvoiceHistory() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [search, setSearch] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);
  const currencySymbol =
    sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || "₹";

  const canUpdate = checkIsPermissionExist(VM_PERMISSIONS, "VM007");
  const canDelete = checkIsPermissionExist(VM_PERMISSIONS, "VM008");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_GET_INVOICES_LIST, {
        params: {
          user_id: userID,
          limit: paginationModel.pageSize,
          offset: paginationModel.page * paginationModel.pageSize,
          search,
          start_date: startDate
            ? dayjs(startDate).format("YYYY-MM-DD")
            : undefined,
          end_date: endDate
            ? dayjs(endDate).format("YYYY-MM-DD")
            : undefined,
        },
      });

      if (res.data?.status_code === 200) {
         const formattedRows = (res.data.invoice_info || []).map((inv, idx) => ({  ...inv,
              s_no: idx + 1 + (paginationModel.page * paginationModel.pageSize),   }));

      setInvoices(formattedRows);
      setRowCount(res.data.count || 0);
      } else {
        setInvoices([]);
        setRowCount(0);
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setInvoices([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    if (!canMutateRecord(VM_PERMISSIONS, "VM007", row)) {
      toast.error(RECORD_OWNERSHIP_DENIED_MESSAGE);
      return;
    }
    setSelectedInvoice({
      enquiry_id: row.enquiry_id,
      invoice_id: row.invoice_id,
    });
  };

  const handleDelete = async (row) => {
    if (!canMutateRecord(VM_PERMISSIONS, "VM008", row)) {
      toast.error(RECORD_OWNERSHIP_DENIED_MESSAGE);
      return;
    }
    const confirmed = window.confirm(`Delete invoice ${row.invoice_id}?`);
    if (!confirmed) return;
    try {
      setDeletingId(row.invoice_id);
      await axios.get(`${API_DELETE_INVOICE}?invoice_id=${row.invoice_id}`);
      fetchInvoices();
    } catch (err) {
      console.error("Delete error:", err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleSearchSubmit = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    fetchInvoices();
  };

  const handleClearFilters = () => {
    setSearch("");
    setStartDate(null);
    setEndDate(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    fetchInvoices();
  };

  const renderWrapCell = (params, textAlign = "left") => {
    const text = params.formattedValue ?? params.value ?? "-";
    return (
      <Tooltip title={String(text)} arrow>
        <Box
          component="span"
          sx={{
            display: "block",
            width: "100%",
            height:'100%',
            minWidth: 0,
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            // lineHeight: 1.35,
            textAlign,
            ...align_center,
            ...align_left
          }}
        >
          {text}
        </Box>
      </Tooltip>
    );
  };

  // ✅ BASE COLUMNS (S.No INCLUDED HERE)
  const baseColumns = [
    renderSNo(),
    {
      field: "invoice_id",
      headerName: "Invoice ID",
      minWidth: 72,
      flex: 1.35,
      renderCell: (params) => renderWrapCell(params),
    },
    // {
    //   field: "enquiry_id",
    //   headerName: "Enquiry ID",
    //   minWidth: 72,
    //   flex: 1.35,
    //   renderCell: (params) => renderWrapCell(params),
    // },
    {
      field: "customer_name",
      headerName: "Customer",
      minWidth: 64,
      flex: 1,
      renderCell: (params) => renderWrapCell(params),
    },
    {
      field: "final_payable_amount",
      headerName: "Final Amount",
      minWidth: 68,
      flex: 1,
      align: "left",
      headerAlign: "left",
      valueFormatter: (params) =>
        formatAmount(params, currencySymbol),
    },
    {
      field: "paid_amount",
      headerName: "Amount Paid",
      minWidth: 68,
      flex: 1,
      align: "left",
      headerAlign: "left",
      valueFormatter: (params) =>
        formatAmount(params, currencySymbol),
    },
    {
      field: "pending_amount",
      headerName: "Pending Amount",
      minWidth: 68,
      flex: 1,
      align: "left",
      headerAlign: "left",
      valueFormatter: (params) =>
        formatAmount(params, currencySymbol),
    },
    {
      field: "created_by",
      headerName: "Created By",
      minWidth: 110,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const userObj = params.value || params.row?.created_by || null;
        if (!userObj) {
          return renderWrapCell({ ...params, value: "-" });
        }
        const role = userObj.user_type || userObj.userType || "";
        return (
          <Box
            sx={{
              ...align_left,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              minWidth: 0,
            }}
          >
            <CreatedByCell user={userObj} />
            {role ? (
              <MUITypography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: 12, lineHeight: 1.3 }}
              >
                {role}
              </MUITypography>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: "created_date",
      headerName: "Created Date",
      minWidth: 80,
      flex: 1.05,
      align: "center",
      headerAlign: "center",
       valueFormatter: (params) =>
        formatDateOnlyFromTDate(params),
      // renderCell: (params) =>
      //   renderWrapCell(
      //     { ...params, value: params.value ? formatDateOnlyFromTDate(params.value) : "-" },
      //     "center"
      //   ),
    },
  ];

  // ✅ ACTION COLUMN
  const actionsColumn = {
    field: "actions",
    headerName: "Actions",
    minWidth: 84,
    flex: 0,
    width: 92,
    sortable: false,
    filterable: false,
    align: "left",
    headerAlign: "left",
    renderCell: (params) => (
      <Box display="flex" gap={0.5} justifyContent="flex-start">
        {canMutateRecord(VM_PERMISSIONS, "VM007", params.row) && (
          <Tooltip title="Edit" arrow>
            <IconButton
              color="primary"
              onClick={() => handleEdit(params.row)}
              size="small"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
        {canMutateRecord(VM_PERMISSIONS, "VM008", params.row) && (
          <Tooltip title="Delete" arrow>
            <span>
              <IconButton
                color="error"
                onClick={() => handleDelete(params.row)}
                size="small"
                disabled={deletingId === params.row.invoice_id}
              >
                {deletingId === params.row.invoice_id ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    ),
  };

  // ✅ PRESERVE CUSTOM renderCell (CRITICAL FIX)
  const columns = [
    ...baseColumns.map((col) => ({
      ...col,
      renderCell: col.renderCell
        ? col.renderCell
        : (params) => renderWrapCell(params, "left"),
    })),
    ...(canUpdate || canDelete ? [actionsColumn] : []),
  ];

  const mobileColumnVisibilityModel = {
    s_no: false,
    enquiry_id: false,
    paid_amount: false,
    pending_amount: false,
    created_date: false,
  };

  return (
   <Box sx={{ width: "100%", minWidth: 0,p:3 }}>
      <MUITypography variant="pageTitle" label="Invoice History" sx={{ ...vmPageTitleSx, pb: 1 }}/>

      <VMFilters
        searchValue={search}
        searchOnChange={(e) => setSearch(e.target.value)}
        fromDateValue={startDate ? dayjs(startDate) : null}
        fromDateOnChange={setStartDate}
        toDateValue={endDate ? dayjs(endDate) : null}
        toDateOnChange={setEndDate}
        clearOnChange={handleClearFilters}
        applyOnChange={handleSearchSubmit}
        isToalCountDisplay
        totalCount={rowCount}
      />

      <Box mt={2}>
        {checkIsPermissionExist(VM_PERMISSIONS, "VM006") 
        ? (
          <ReusableDataGrid
            rows={invoices}
            columns={columns}
            getRowId={(row) => row.invoice_id}
            columnVisibilityModel={isMobile ? mobileColumnVisibilityModel : {}}
            paginationMode="server"
            rowCount={rowCount}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            onPageChange={(newPage) =>
              setPaginationModel((prev) => ({ ...prev, page: newPage }))
            }
            onPageSizeChange={(newPageSize) =>
              setPaginationModel((prev) => ({ ...prev, pageSize: newPageSize, page: 0 }))
            }
            loading={loading}
            pageSizeOptions={[5, 10, 25]}
            autoHeight
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            hideFooterSelectedRowCount
            headerBackgroundColor="#1E3A8A"
            headerTextColor="#FFFFFF"
          />
        )
        :  <Box sx={{ mt: 4 }}>
            <NoPermission message={ACCESS_RESTRICTED_MESSAGE} />  
        </Box>
      }
      </Box>

      <UpdatePaymentDrawer
        open={!!selectedInvoice}
        onClose={() => {
          setSelectedInvoice(null);
          fetchInvoices();
        }}
        enquiry_id={selectedInvoice?.enquiry_id}
        user_id={userID}
        invoice_id={selectedInvoice?.invoice_id}
      />
    </Box>
  );
}

export default VmInvoiceHistory;
